import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, Lock, User, Building2, ArrowRight, Phone } from "lucide-react";
import AuthLayout from "../components/AuthLayout";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../hooks/use-toast";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    company: "",
    password: "",
    agree: false
  });
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.agree) {
      toast({ title: "Please accept the terms", description: "You must agree to the conditions." });
      return;
    }
    if ((form.password || "").length < 6) {
      toast({ title: "Password too short", description: "Use at least 6 characters." });
      return;
    }
    setSubmitting(true);
    try {
      await register({
        email: form.email,
        password: form.password,
        name: `${form.firstName} ${form.lastName}`.trim() || form.email,
        phone: form.phone,
        company: form.company,
        role: "customer"
      });
      toast({ title: "Account created", description: "Welcome to 518 Courier!" });
      navigate("/customer-dashboard");
    } catch (err) {
      toast({
        title: "Couldn't create account",
        description: err?.message?.replace("Firebase: ", "") || "Try a different email.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout
      variant="customer"
      title="Create your account"
      subtitle="Book deliveries faster and track every shipment in one place."
      footer={
        <>
          Already have an account?{" "}
          <Link to="/login" className="text-[#f59321] font-semibold hover:underline">Sign in</Link>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Field label="First name" icon={User} value={form.firstName} onChange={(v) => update("firstName", v)} placeholder="Jane" required />
          <Field label="Last name" value={form.lastName} onChange={(v) => update("lastName", v)} placeholder="Doe" required />
        </div>
        <Field label="Email" type="email" icon={Mail} value={form.email} onChange={(v) => update("email", v)} placeholder="jane@company.com" required />
        <Field label="Phone" type="tel" icon={Phone} value={form.phone} onChange={(v) => update("phone", v)} placeholder="(518) 555-0123" required />
        <Field label="Company (optional)" icon={Building2} value={form.company} onChange={(v) => update("company", v)} placeholder="Acme Co." />
        <Field label="Password" type="password" icon={Lock} value={form.password} onChange={(v) => update("password", v)} placeholder="At least 8 characters" required />
        <label className="flex items-start gap-2 text-sm text-slate-600 cursor-pointer pt-1">
          <Checkbox
            checked={form.agree}
            onCheckedChange={(v) => update("agree", v)}
            className="mt-0.5 data-[state=checked]:bg-[#f59321] data-[state=checked]:border-[#f59321]"
          />
          <span>
            I agree to the{" "}
            <Link to="/conditions" className="text-[#f59321] font-semibold hover:underline">
              Terms & Conditions
            </Link>
            .
          </span>
        </label>
        <Button
          type="submit"
          disabled={submitting}
          className="btn-lift w-full bg-[#f59321] hover:bg-[#e07f10] text-white font-semibold rounded-full h-12 shadow-md disabled:opacity-60"
        >
          {submitting ? "Creating account…" : "Create account"}
          <ArrowRight className="w-4 h-4 ml-1" />
        </Button>
      </form>
    </AuthLayout>
  );
}

function Field({ label, icon: Icon, type = "text", value, onChange, placeholder, required }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[#0f2847] font-semibold">{label}{required && " *"}</Label>
      <div className="relative">
        {Icon && <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />}
        <Input
          type={type}
          required={required}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={`h-12 rounded-xl border-slate-200 focus:border-[#f59321] focus:ring-[#f59321] ${Icon ? "pl-10" : ""}`}
        />
      </div>
    </div>
  );
}
